import { QuestRoutine } from "@joonapp/web-shared"
import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import { useEffect } from "react"
import { useState } from "react"

dayjs.extend(timezone)
dayjs.extend(utc)

/**
 * Determines the time of day routine (morning, afternoon, night) based on hour
 * @param time - Time string in "HH:MM" format
 * @returns Appropriate QuestRoutine value
 */
export const getRoutineFromTimeString = (time: string) => {
  const [hours] = time.split(":").map(Number)

  if (hours >= 0 && hours < 12) {
    return QuestRoutine.MORNING
  } else if (hours >= 12 && hours < 18) {
    return QuestRoutine.AFTERNOON
  } else {
    return QuestRoutine.NIGHT
  }
}

/**
 * Updates date portion of a datetime while preserving time and timezone
 * @param datetime - Original datetime
 * @param date - New date to use
 * @returns Updated datetime or null
 */
export const updateDateInDatetime = (
  datetime: dayjs.Dayjs | null,
  date: dayjs.Dayjs | null
): dayjs.Dayjs | null => {
  if (!date) return datetime
  if (!datetime) return date

  return datetime.year(date.year()).month(date.month()).date(date.date())
}

/**
 * Updates time portion of a datetime while preserving date and timezone
 * @param datetime - Original datetime
 * @param time - Time string in "HH:MM" format
 * @returns Updated datetime or null
 */
export const updateTimeInDatetime = (
  datetime: dayjs.Dayjs | null,
  time: string
): dayjs.Dayjs | null => {
  if (!time) return datetime

  const [hours, minutes] = time.split(":").map(Number)

  if (!datetime) {
    return dayjs().hour(hours).minute(minutes).second(0).millisecond(0)
  }

  return datetime.hour(hours).minute(minutes).second(0).millisecond(0)
}

/**
 * Hook that provides current time in specified timezone, updating every minute
 * @param timeZone - IANA timezone string (e.g., "America/New_York")
 * @param dependencies - Array of dependencies that trigger time updates
 * @returns Current time string in "h:mm AM/PM" format
 */
export const useLocalTime = (timeZone: string, dependencies: any[] = []) => {
  // update the time every minute
  const [time, setTime] = useState(getLocalTime(timeZone))
  useEffect(() => {
    const interval = setInterval(() => {
      setTime(getLocalTime(timeZone))
    }, 1000 * 60)
    return () => clearInterval(interval)
  }, [timeZone])

  useEffect(() => {
    setTime(getLocalTime(timeZone))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies)

  function getLocalTime(timeZone: string) {
    return dayjs().tz(timeZone).format("h:mm A")
  }

  return time
}

/**
 * Converts time string to dayjs datetime, optionally in specified timezone
 * @param time - Time string in 12-hour or 24-hour format
 * @param timezone - Optional IANA timezone string
 * @returns Formatted datetime string or null
 */
export const convertTimeToDatetimeString = (
  time: string | null,
  timezone?: string | null
) => {
  if (!time) return null

  let datetimeObj = dayjs(dayjs().format("YYYY/MM/DD") + " " + time)
  if (timezone) datetimeObj = datetimeObj.tz(timezone, true)

  return datetimeObj.format()
}

/**
 * Generates human-readable relative time until a future datetime
 * @param datetime - Target datetime string
 * @param includeDate - Whether to include date in output for non-today/tomorrow times
 * @returns Formatted relative time string (e.g., "In 30 minutes", "Tomorrow at 3:00 PM")
 */
export const getRelativeTimeUntil = (datetime: string, includeDate = false) => {
  const messageTime = dayjs(datetime)
  const now = dayjs()
  const diffMinutes = messageTime.diff(now, "minutes")

  const isToday = messageTime.isSame(now, "day")
  const isTomorrow = messageTime.isSame(now.add(1, "day"), "day")

  if (diffMinutes < 60)
    return `In ${diffMinutes} minute${diffMinutes === 1 ? "" : "s"}`
  if (isToday) return `Today at ${messageTime.format("h:mm A")}`
  if (isTomorrow) return `Tomorrow at ${messageTime.format("h:mm A")}`

  return `${messageTime.format("dddd")} ${
    includeDate ? messageTime.format("MMM D, ") : ""
  }at ${messageTime.format("h:mm A")}`
}

/**
 * Formats a datetime in a specific timezone
 * @param datetime - Datetime string to format
 * @param timezone - IANA timezone string
 * @returns Time string in "h:mm AM/PM" format or null
 */
export const getFormattedTimeInTimezone = (
  datetime: string | null,
  timezone: string | null
) => {
  if (!datetime || !timezone) return null
  return dayjs(datetime).tz(timezone).format("h:mm A")
}

export const convertTo24HourFormat = (timeValue: string): string => {
  return dayjs(convertTimeToDatetimeString(timeValue)).format("HH:mm")
}
